<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: DescendantTree                                        |
| Section: views/trees                                             |
| Purpose: Descendant tree route for child-line exploration.       |
+------------------------------------------------------------------+
-->
<template>
    <div class="surname-tree-container">
        <div id="zoom-buttons">
            <button @click="zoomIn"><img src="@/assets/icons/zoom_in_red.svg" alt="Zoom In"></button>
            <button @click="zoomOut"><img src="@/assets/icons/zoom_out_red.svg" alt="Zoom Out"></button>
        </div>

        <div class="descendant-list-container">
            <div class="descendant-list-header">
                <p>{{ $t('treeViews.descendantsOfCount', { count: uniqueDescendants, name: probandusName }) }}</p>
                <a class="current-person" :href="localePath(`/person/${personId}`)">{{ probandusName }}</a>
            </div>
            <div v-for="item in filteredDescendantList" :key="item.type === 'label' ? `label-${item.depth}` : item.id">
                <CollapsiblePanel v-if="item.type === 'label'" :label="item.label" arrowColor="var(--brand-color-1)"
                    :isAncestorTree="true">
                    <div v-for="descendant in filteredDescendantList.filter(d => d.type === 'descendant' && d.depth === item.depth)"
                        :key="descendant.id" class="descendant-item"
                        :class="{ highlighted: descendant.id === selectedDescendant }"
                        @click="!descendant.placeholder && selectDescendant(descendant.id)">
                        {{ descendant.name }}

                        <div v-if="!descendant.placeholder && descendant.id === selectedDescendant && selectedPersonDetails"
                            class="secondary-label">
                            <button @click.stop="goToSearch(descendant.id)">
                                {{ $t('treeViews.viewProfile') }}
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17"
                                    fill="var(--brand-color-1)">
                                    <path
                                        d="M15.7063 9.20627C16.0969 8.81565 16.0969 8.18127 15.7063 7.79065L11.7063 3.79065C11.3156 3.40002 10.6812 3.40002 10.2906 3.79065C9.9 4.18127 9.9 4.81565 10.2906 5.20627L12.5844 7.50002H1C0.446875 7.50002 0 7.9469 0 8.50002C0 9.05315 0.446875 9.50002 1 9.50002H12.5844L10.2906 11.7938C9.9 12.1844 9.9 12.8188 10.2906 13.2094C10.6812 13.6 11.3156 13.6 11.7063 13.2094L15.7063 9.2094V9.20627Z"
                                        fill="var(--brand-color-1)" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </CollapsiblePanel>
            </div>
        </div>

        <div id="surname-tree-container"></div>
    </div>
</template>

<script>
import * as d3 from "d3";
import { useAuth } from '@/composables/useAuth';
import { getDescendants } from '@/api/personService';
import CollapsiblePanel from '@/components/CollapsiblePanel.vue';
import { useLocale } from '@/composables/useLocale';
import { getPersonNodeKey, isLivingPlaceholder, isPrivateLivingPerson } from '@/utils/personPrivacy';

export default {
    name: 'DescendantTree',
    components: { CollapsiblePanel },
    props: {
        personId: {
            type: String,
            required: true
        }
    },
    setup() {
        // Injecting properties from App.vue using the useAuth composable
        const { authToken, username, logout } = useAuth();
        const { localePath } = useLocale();

        return {
            authToken,
            username,
            logout,
            localePath,
        };
    },
    data() {
        return {
            selectedGenerations: 10, // Matches the backend recursion safety limit.
            zoom: null,
            treeData: null,
            descendantList: [],
            uniqueDescendants: 0,
            selectedDescendant: "",
            dropdownOpen: null,
            probandusName: "",
            selectedPersonDetails: null,
        };
    },
    mounted() {
        this.fetchDescendantGraph();
        window.addEventListener('resize', this.resizeTree);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.resizeTree);
    },
    methods: {
        async fetchDescendantGraph() {
            try {
                const resp = await getDescendants(this.personId, this.selectedGenerations);
                if (!resp || !resp.personID) {
                    throw new Error("Invalid API response: Missing root person data.");
                }

                this.probandusName = `${resp.firstname || this.$t('treeViews.unknown')} ${resp.lastname || this.$t('treeViews.unknown')}`;

                const nodes = [];
                const links = [];
                const visited = new Set();

                const flatten = (person, depth = 0) => {
                    const personNodeID = getPersonNodeKey(person);
                    if (!person || !personNodeID || visited.has(personNodeID)) return;
                    visited.add(personNodeID);
                    const placeholder = isLivingPlaceholder(person);
                    const isPrivate = isPrivateLivingPerson(person, this.authToken);

                    nodes.push({
                        id: personNodeID,
                        name: placeholder
                            ? this.$t('privacy.livingPlaceholder')
                            : isPrivate ? this.$t('nav.privacyHidden') : `${person.firstname} ${person.lastname}`,
                        photo: person.photoPath || null,
                        sex: person.sex,
                        private: isPrivate,
                        placeholder,
                        depth,
                    });

                    if (person.descendants && person.descendants.length > 0) {
                        person.descendants.forEach(child => {
                            links.push({ source: personNodeID, target: getPersonNodeKey(child) });
                            flatten(child, depth + 1);
                        });
                    }
                };

                flatten(resp);
                this.treeData = { nodes, links };

                const generationLabels = [
                    this.$t('treeViews.generationLabels.probandus'),
                    this.$t('treeViews.generationLabels.children'),
                    this.$t('treeViews.generationLabels.grandchildren'),
                    this.$t('treeViews.generationLabels.greatGrandchildren'),
                    this.$t('treeViews.generationLabels.secondGreatGrandchildren'),
                    this.$t('treeViews.generationN', { n: 5 }),
                    this.$t('treeViews.generationN', { n: 6 }),
                    this.$t('treeViews.generationN', { n: 7 }),
                    this.$t('treeViews.generationN', { n: 8 }),
                    this.$t('treeViews.generationN', { n: 9 }),
                    this.$t('treeViews.generationN', { n: 10 })
                ];

                const grouped = [];
                nodes.sort((a, b) => a.depth - b.depth).forEach(node => {
                    if (!grouped.some(item => item.type === "label" && item.depth === node.depth)) {
                        grouped.push({
                            type: "label",
                            depth: node.depth,
                            label: generationLabels[node.depth] || this.$t('treeViews.generationN', { n: node.depth }),
                        });
                    }
                    grouped.push({ type: "descendant", ...node });
                });

                this.descendantList = grouped;
                this.uniqueDescendants = nodes.length;

                d3.select("#surname-tree-container").selectAll("*").remove();
                this.createSurnameTree(this.treeData);
            } catch (err) {
                console.error("Error fetching descendant graph:", err);
                alert(this.$t('treeViews.descendantsLoadError'));
            }
        },

        goToSearch(personID) {
            this.$router.push(this.localePath(`/person/${encodeURIComponent(personID)}`));
        },

        async selectDescendant(id) {
            if (this.treeData?.nodes?.find(node => node.id === id)?.placeholder) return;
            if (this.selectedDescendant === id) {
                this.selectedDescendant = "";
                this.selectedPersonDetails = null;
                this.highlightAncestors();
            } else {
                this.selectedDescendant = id;
                this.highlightAncestors();
                this.centerOnNode(id);

                try {
                    const resp = await getDescendants(id, 0);
                    this.selectedPersonDetails = {
                        name: `${resp.firstname || this.$t('treeViews.unknown')} ${resp.lastname || this.$t('treeViews.unknown')}`,
                        birthDate: resp.birthdatetr || this.$t('treeViews.unknown'),
                        deathDate: resp.deathdatetr || this.$t('treeViews.unknown'),
                        sex: resp.sex || this.$t('treeViews.unknown'),
                    };
                } catch (error) {
                    console.error("Error fetching person details:", error);
                    this.selectedPersonDetails = null;
                }

                const list = this.$el.querySelector(".descendant-list-container");
                const item = list.querySelector(".descendant-item.highlighted");
                if (item) {
                    const offset = 8;
                    const topPosition = item.offsetTop - list.offsetTop - offset;
                    list.scrollTo({ top: topPosition, behavior: "smooth" });
                }
            }
        },

        highlightAncestors() {
            const svg = d3.select("#surname-tree-container svg");
            if (!svg.empty()) {
                if (this.selectedDescendant) {
                    const pathToRoot = [];
                    const findPath = (nodeId, links) => {
                        let currentId = nodeId;
                        while (currentId) {
                            pathToRoot.push(currentId);
                            const parentLink = links.find(link => link.target === currentId);
                            currentId = parentLink ? parentLink.source : null;
                        }
                    };
                    findPath(this.selectedDescendant, this.treeData.links);

                    const pathLinks = this.treeData.links.filter(
                        link => pathToRoot.includes(link.source) && pathToRoot.includes(link.target)
                    );

                    svg.selectAll(".nodes circle")
                        .classed("highlighted", d => pathToRoot.includes(d.id))
                        .classed("dimmed", d => !pathToRoot.includes(d.id));

                    svg.selectAll(".links line")
                        .classed("highlighted", d => pathLinks.some(
                            link => link.source === d.source.id && link.target === d.target.id
                        ))
                        .classed("dimmed", d => !pathLinks.some(
                            link => link.source === d.source.id && link.target === d.target.id
                        ));

                    svg.selectAll(".labels text")
                        .attr("fill", d => pathToRoot.includes(d.id) ? "#000" : "#AAA");
                } else {
                    svg.selectAll(".nodes circle").classed("highlighted", false).classed("dimmed", false);
                    svg.selectAll(".links line").classed("highlighted", false).classed("dimmed", false);
                    svg.selectAll(".labels text").attr("fill", "#000");
                }
            }
        },

        resizeTree() {
            const container = d3.select('.surname-tree-container').node();
            const width = container.offsetWidth;
            const height = container.offsetHeight;
            const svg = d3.select('#surname-tree-container svg');
            if (!svg.empty()) {
                svg.attr('width', width).attr('height', height);
            }
        },

        centerOnNode(personID) {
            const svg = d3.select('#surname-tree-container svg');
            const node = svg.select(`.nodes circle[data-id="${personID}"]`);
            if (node.empty()) return;
            const { x, y } = node.datum();
            const container = d3.select('.surname-tree-container').node();
            const w = container.clientWidth, h = container.clientHeight;
            const scale = 1.5;
            const tx = w / 2 - x * scale, ty = h / 2 - y * scale;
            svg.transition().duration(750)
                .call(this.zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(scale));
        },

        zoomIn() {
            const svg = d3.select('#surname-tree-container svg');
            svg.transition().duration(500).call(this.zoom.scaleBy, 1.5);
        },

        zoomOut() {
            const svg = d3.select('#surname-tree-container svg');
            svg.transition().duration(500).call(this.zoom.scaleBy, 0.5);
        },

        createSurnameTree(data) {
            const container = d3.select('.surname-tree-container').node();
            const width = container.offsetWidth;
            const height = container.offsetHeight;

            this.zoom = d3.zoom().on('zoom', event => {
                d3.select('#surname-tree-container g').attr('transform', event.transform);
            });

            const svg = d3.select('#surname-tree-container')
                .append('svg')
                .attr('width', width)
                .attr('height', height)
                .call(this.zoom)
                .append('g');

            const nodes = data.nodes;
            const links = data.links.map(l => ({
                source: nodes.find(node => node.id === l.source),
                target: nodes.find(node => node.id === l.target)
            }));

            const link = svg.append('g')
                .attr('class', 'links')
                .selectAll('line')
                .data(links)
                .enter()
                .append('line')
                .attr('stroke', '#aaa')
                .attr('stroke-width', 1);

            const node = svg.append('g')
                .attr('class', 'nodes')
                .selectAll('circle')
                .data(nodes)
                .enter()
                .append('circle')
                .attr('data-id', d => d.id)
                .attr('r', 8)
                .attr('fill', d => d.sex === 'M' ? '#ae1e25' : d.sex === 'F' ? '#ae1e25' : '#ae1e25')
                .attr('stroke', '#fff')
                .attr('stroke-width', 1.5)
                .classed('dimmed', d => d.private)
                .call(d3.drag()
                    .on('start', (event, d) => {
                        if (!event.active) simulation.alphaTarget(0.3).restart();
                        d.fx = d.x; d.fy = d.y;
                    })
                    .on('drag', (event, d) => { d.fx = event.x; d.fy = event.y; })
                    .on('end', (event, d) => {
                        if (!event.active) simulation.alphaTarget(0);
                        d.fx = null; d.fy = null;
                    })
                )
                .on('click', (event, d) => {
                    event.stopPropagation();
                    this.selectDescendant(d.id);
                });

            d3.select('#surname-tree-container').select('svg')
                .on('click', () => {
                    this.selectedDescendant = "";
                    this.highlightAncestors();
                });

            svg.append('g')
                .attr('class', 'labels')
                .selectAll('text')
                .data(nodes)
                .enter()
                .append('text')
                .attr('x', 12)
                .attr('dy', '.35em')
                .style('font-size', '12px')
                .text(d => d.name);

            const simulation = d3.forceSimulation(nodes)
                .force('link', d3.forceLink(links).id(d => d.id).distance(100))
                .force('charge', d3.forceManyBody().strength(-100))
                .force('center', d3.forceCenter(width / 2, height / 2))
                .on('tick', () => {
                    link
                        .attr('x1', d => d.source.x)
                        .attr('y1', d => d.source.y)
                        .attr('x2', d => d.target.x)
                        .attr('y2', d => d.target.y);

                    node.attr('cx', d => d.x).attr('cy', d => d.y);

                    svg.selectAll('text')
                        .attr('x', d => d.x + 12)
                        .attr('y', d => d.y);
                })
                .on('end', () => {
                    this.centerOnNode(this.personId);
                });

            simulation.force('link').links(links);
        }
    },
    computed: {
        filteredDescendantList() {
            return this.descendantList.filter(item => {
                if (item.type === 'descendant' && item.depth === 0) return false;
                if (item.type === 'label' && item.depth === 0) return false;
                return true;
            });
        }
    },
};
</script>

<style scoped>
.surname-tree-container {
    height: 100vh !important;
}

.descendant-list-container {
    height: fit-content;
    max-height: calc(100% - 48px);
    overflow: auto;
    border-radius: 16px;
    padding: 2px 0;
    background-color: #fff;
    width: 100%;
    max-width: 360px;
    position: absolute;
    top: 24px;
    left: 24px;
    z-index: 10;
    box-shadow: var(--shadow-card-lg);
}

.descendant-list-header {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;
}

.descendant-list-header p {
    font-size: 14px;
    font-weight: 900;
    text-transform: uppercase;
}

.current-person {
    font-weight: 700;
    color: var(--brand-color-1);
    font-size: 24px;
    text-decoration: none;
}

.current-person:hover {
    text-decoration: underline;
}

.surname-tree-container:hover {
    cursor: grab;
}

.surname-tree-container:active {
    cursor: grabbing;
}

#zoom-buttons {
    position: absolute;
    bottom: 24px;
    right: 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 100;
}

#zoom-buttons button {
    padding: 0;
    cursor: pointer;
    background-color: transparent;
    border: none;
    border-radius: var(--border-radius-pill);
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 0px var(--brand-color-1);
}

#zoom-buttons button:hover {
    box-shadow: 0px 0px 0px 4px var(--brand-color-1);
}

#surname-tree-container {
    width: 100%;
    height: 100%;
    overflow: hidden;
}

.links line {
    stroke: #aaa;
    stroke-width: 1px;
}

.nodes circle {
    cursor: pointer;
    stroke: #fff;
    stroke-width: 1.5px;
}

.nodes circle:hover {
    stroke: black;
}

.labels text {
    font-family: Arial, sans-serif;
    pointer-events: none;
}

.highlighted {
    stroke: black !important;
    fill: #ae1e25;
}

.nodes circle.descendant-highlighted {
    fill: #FFCC33;
}

.secondary {
    stroke: black !important;
    fill: #FFCC33 !important;
}

.dimmed {
    stroke: #ccc !important;
    fill: #ddd;
    z-index: 1;
}

.links line.highlighted {
    stroke: black !important;
    z-index: 10;
}

.links line.secondary {
    stroke: black !important;
}

.links line.dimmed {
    stroke: #ddd !important;
    z-index: 1;
}

.generation-label {
    border-bottom: 1px solid #f0f0f0;
    padding: 8px 24px;
    font-weight: 700;
    color: var(--brand-text-dark);
    font-size: 14px;
    text-transform: uppercase;
}

.generation-label strong {
    font-weight: 900;
}

.descendant-item {
    position: relative;
    padding: 8px 24px 8px 48px;
    cursor: pointer;
    font-weight: 600;
    border-bottom: 1px solid #f0f0f0;
}

.descendant-item:hover .search-button {
    display: inline-block;
}

.descendant-item:hover {
    background-color: #f0f0f0;
    text-decoration: underline;
}

.descendant-item.highlighted {
    background-color: #f0f0f0;
    color: var(--brand-color-1);
    font-weight: bold;
    z-index: 10;
    padding: 16px 24px 16px 48px;
}

.descendant-item.highlighted:hover {
    text-decoration: none;
}

.secondary-label,
.secondary-item {
    color: var(--brand-text-dark);
    font-weight: 400;
    font-size: 12px;
}

.secondary-label {
    margin-top: 8px;
}

.secondary-label button {
    border-radius: var(--border-radius-sm);
    border: 1px solid var(--brand-color-1);
    color: var(--brand-color-1);
    font-weight: 600;
    cursor: pointer;
    font-size: 14px;
    padding: 4px 8px;
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
}

.secondary-label button:hover {
    text-decoration: underline;
}

.secondary-label button svg {
    stroke: none !important;
}

.secondary-list {
    padding-left: 0;
    list-style: none;
}

.secondary-item {
    padding-left: 16px;
    position: relative;
}

.secondary-item::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    background-color: var(--brand-color-3);
    border-radius: var(--border-radius-pill);
    border: 1px solid var(--brand-text-dark);
}

@media (max-width: 860px) {
    .descendant-list-container {
        display: none;
    }
}
</style>
